<script setup lang="ts">
import {
  JOURNAL_ADVERTISING_URL,
  getJournalEditionUrl,
  getJournalTabEditions,
  getJournalTabUrl,
  getJournalTabs,
} from '~/shared/lib/journal-routes.const';
import { useJournalPage } from '../lib/useJournalPage';
import {
  useJournalAdvertising,
  useJournalIssues,
  useJournalRecommended,
} from '../lib/useJournalData';
import { useJournalSeo } from '../lib/seo';
import {
  JOURNAL_ADVERTISING_TAB_KEY,
  JOURNAL_ADVERTISING_TAB_LABEL,
  type JournalTabItem,
} from '../lib/journal.const';
import JournalTabs from './JournalTabs.vue';
import JournalEditionFilter from './JournalEditionFilter.vue';
import JournalHero from './JournalHero.vue';
import JournalIssuesPaged from './JournalIssuesPaged.vue';
import JournalAdvertisingEditions from './JournalAdvertisingEditions.vue';
import JournalAdvertisingContacts from './JournalAdvertisingContacts.vue';
import JournalRecommended from './JournalRecommended.vue';

const { reference, target } = await useJournalPage();

/**
 * The tab of the page. An edition page names it through the chip row it sits
 * in — /journal/guides/cre-retail opens with "Архив" selected. Null only on
 * the media kit, which is not a row of the reference
 */
const activeTab = computed(() =>
  target.value && target.value.kind !== 'advertising' ? target.value.tab : null,
);

const activeEdition = computed(() =>
  target.value?.kind === 'edition' ? target.value.edition : null,
);

const isAdvertisingTab = computed(() => target.value?.kind === 'advertising');

const title = computed(
  () => activeTab.value?.name ?? JOURNAL_ADVERTISING_TAB_LABEL,
);

useJournalSeo(
  activeEdition,
  title,
  () => {
    if (activeEdition.value) {
      return getJournalEditionUrl(reference.value, activeEdition.value);
    }

    return activeTab.value
      ? getJournalTabUrl(reference.value, activeTab.value)
      : JOURNAL_ADVERTISING_URL;
  },
  () => activeTab.value?.description,
);

/** The tab bar: the editors' tabs in their order, the media kit last */
const tabs = computed<JournalTabItem[]>(() => [
  ...getJournalTabs(reference.value).map((tab) => ({
    key: String(tab.id),
    label: tab.name,
    to: getJournalTabUrl(reference.value, tab),
  })),
  {
    key: JOURNAL_ADVERTISING_TAB_KEY,
    label: JOURNAL_ADVERTISING_TAB_LABEL,
    to: JOURNAL_ADVERTISING_URL,
  },
]);

const activeTabKey = computed(() =>
  activeTab.value ? String(activeTab.value.id) : JOURNAL_ADVERTISING_TAB_KEY,
);

/**
 * What a tab shows follows from its data and two settings (ADR 0001, decision
 * 26). Chips appear once there is something to choose between — "Книга" holds
 * one book and has none. The grid shows whatever the hero left over, so a tab
 * of a single issue ends with the hero. The hero and the recommendations are
 * the editors' call: nothing in the data tells "Архив" from "Журналы"
 */
const tabEditions = computed(() =>
  activeTab.value
    ? getJournalTabEditions(reference.value, activeTab.value)
    : [],
);

const hasChips = computed(() => tabEditions.value.length > 1);

const hasHero = computed(() => Boolean(activeTab.value?.show_hero));

const hasRecommended = computed(
  () => isAdvertisingTab.value || Boolean(activeTab.value?.show_recommended),
);

/**
 * One query feeds both the hero and the grid: a chip narrows it to its edition,
 * "Все" leaves the whole tab. The endpoint sorts newest first and pages, so the
 * selection arrives in portions rather than whole
 */
const [
  { issues, hasMore, isLoading, isLoadingFirstPage, loadMore },
  recommended,
  advertising,
] = await Promise.all([
  useJournalIssues({
    edition: () => activeEdition.value?.id,
    // The media kit has no grid. Id 0 belongs to no tab, so the query comes
    // back empty instead of fetching the first page of every issue
    tab: () => (activeEdition.value ? undefined : (activeTab.value?.id ?? 0)),
    lazy: true,
  }),
  useJournalRecommended(),
  useJournalAdvertising(),
]);

/**
 * The hero is the newest issue of the current selection, not a flagged one:
 * pick CRE in the chips and you get its latest number, the way cre.ru behaves
 * today (ADR 0001, decision 16)
 */
const heroIssue = computed(() =>
  hasHero.value ? (issues.value[0] ?? null) : null,
);

/** Everything the hero does not already show */
const gridIssues = computed(() =>
  heroIssue.value ? issues.value.slice(1) : issues.value,
);
</script>

<template>
  <div :class="$style.page">
    <section :class="$style.top">
      <div :class="$style.header">
        <h1 :class="$style.title">{{ title }}</h1>
        <JournalTabs :items="tabs" :active="activeTabKey" />
        <JournalEditionFilter
          v-if="activeTab && hasChips"
          :active="activeEdition"
          :tab="activeTab"
          :class="$style.editions"
        />
      </div>
      <div v-if="activeTab && isLoadingFirstPage" :class="$style.loader">
        <Loader small />
      </div>
      <JournalHero v-else-if="heroIssue" :issue="heroIssue" />
      <JournalAdvertisingEditions
        v-if="isAdvertisingTab && advertising"
        :block="advertising"
      />
    </section>

    <JournalAdvertisingContacts v-if="isAdvertisingTab" />

    <section
      v-if="activeTab && !isLoadingFirstPage && gridIssues.length"
      :class="$style.issues"
    >
      <h2 v-if="hasHero" :class="$style.heading">Другие выпуски</h2>
      <JournalIssuesPaged
        :issues="gridIssues"
        :has-more="hasMore"
        :is-loading="isLoading"
        @load-more="loadMore"
      />
    </section>
    <JournalRecommended
      v-if="hasRecommended && recommended"
      :block="recommended"
    />
  </div>
</template>

<style module lang="scss">
.page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: var(--color-white);
}

.top {
  display: flex;
  flex-direction: column;
  background-color: var(--color-gray-bg-2);
}

.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-5x) var(--space-2-5x) var(--space-3x);
}

.title {
  @include text_h1;
  color: var(--color-black-1);
  margin: 0 0 var(--space-3x);
  text-align: center;
}

.editions {
  margin-top: var(--space-2x);
}

.loader {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.issues {
  display: flex;
  flex-direction: column;
}

.heading {
  @include text_h2;
  font-size: 32px;
  color: var(--color-black-1);
  margin: 0;
  padding: var(--space-3x) var(--space-2-5x);
  text-align: center;
}

@media (max-width: 767px) {
  .heading {
    @include text_h2_mobile;
  }
}

@media (max-width: 599px) {
  .header {
    padding: var(--space-4x) var(--space-2x) var(--space-2-5x);
  }
}
</style>
